import React, { useState, useCallback } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import "../../auth.css";

const REQUEST_OTP_URL = process.env.REACT_APP_MFA_POC_REQUEST_OTP_URL; // MFA: backend endpoint

function safeErrorMessage(e) {
  if (!e) return "Something went wrong. Please try again.";
  if (typeof e === "string") return e;
  if (e.message) return e.message;
  return String(e);
}

export default function MfaPocRequestCode() {
  const history = useHistory();
  const location = useLocation();

  // MFA: prefill email from ?email= if present
  const sp = new URLSearchParams(location.search);
  const initialEmail = (sp.get("email") || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!email) {
        setError("Please enter your email address.");
        return;
      }
      if (!REQUEST_OTP_URL) {
        setError("Reset service is not configured. Please contact support."); // MFA: missing env guard
        return;
      }

      setBusy(true);
      setError("");
      setInfo("");

      try {
        const res = await fetch(REQUEST_OTP_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ username: email.trim(), email: email.trim() }),
        });

        let data = null;
        try {
          data = await res.json();
        } catch {
          // ignore JSON parse errors; we'll fallback to generic messages
        }

        if (!res.ok) {
          const msg =
            (data && data.message) ||
            (data && data.error) ||
            "We couldn’t send a code. Please try again.";
          setError(msg);
          return;
        }

        // Lambda always returns a generic success message:
        // "If this email is registered, an OTP has been sent."
        const msg =
          (data && data.message) ||
          "If this email is registered, an OTP has been sent.";
        setInfo(msg);

        // MFA: navigate to the Verify Code screen with email prefilled
        history.push(
          `/auth/mfa/verify?email=${encodeURIComponent(email.trim())}`
        );
      } catch (ex) {
        setError(safeErrorMessage(ex));
      } finally {
        setBusy(false);
      }
    },
    [email, history]
  );

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">MFA: Request Reset Code</h1>
        <p className="ph-hint">
          Enter your email. If it exists, we’ll send a one-time code.
        </p>

        {info && (
          <div className="ph-note" role="status">
            {info}
          </div>
        )}
        {error && (
          <div className="ph-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label className="ph-label" htmlFor="mfa-email">
            Email
          </label>
          <input
            id="mfa-email"
            className="ph-field"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy}
          />

          <button
            className="ph-button"
            type="submit"
            disabled={busy || !email}
          >
            {busy ? "Sending…" : "Send code"}
          </button>
        </form>

        <p className="ph-hint" style={{ marginTop: 12 }}>
          <Link className="ph-link" to="/signin">
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}